import {notification} from 'antd';
import Blocknative from 'bnc-sdk';
import {useEffect, useState} from 'react';
import {BLOCKNATIVE_DAPPID, NETWORK} from 'src/constants';
import processingSvg from './processing.svg';
import {usePrevious} from './usePrevious';

const notify = new Blocknative({
  dappId: BLOCKNATIVE_DAPPID,
  networkId: NETWORK.chainId,
});

enum State {
  Start,
  Sent,
  Pool,
  Confirmed,
  Cancel,
  Failed,
}

const Message = {
  [State.Start]: 'Transaction started',
  [State.Sent]: 'Transaction sent',
  [State.Pool]: 'Transaction processing',
  [State.Confirmed]: 'Transaction completed! Please wait a bit for the block to be confirmed.',
  [State.Cancel]: 'Transaction canceled',
  [State.Failed]: 'Transaction failed',
};

function getStatus(state: State) {
  if ([State.Start, State.Sent].includes(state)) {
    return 'info';
  } else if (state === State.Pool) {
    return 'info';
  } else if (state === State.Confirmed) {
    return 'success';
  } else {
    return 'error';
  }
}

export function useTransactionStatus(hash: string | null) {
  const [state, setState] = useState<State | null>(State.Pool);
  const previousHash = usePrevious(hash);

  useEffect(() => {
    if (previousHash) {
      notify.unsubscribe(previousHash);
    }

    if (hash) {
      const {emitter} = notify.transaction(hash);

      emitter.on('txSent', () => setState(State.Sent));
      emitter.on('txPool', () => setState(State.Pool));
      emitter.on('txConfirmed', (t) => setState(State.Confirmed));
      emitter.on('txCancel', () => setState(State.Cancel));
      emitter.on('txFailed', () => setState(State.Failed));

      return () => {
        notify.unsubscribe(hash);
      };
    }
  }, [hash]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state && hash) {
      const isProcessing = state === State.Pool;
      notification[getStatus(state)]({
        key: hash,
        message: Message[state],
        duration: isProcessing ? null : 5.5,
        icon:
          state === State.Pool ? (
            <img style={{height: 28}} src={processingSvg} alt='processing' />
          ) : undefined,
      });
    }
  }, [state, hash]);

  return state;
}
